import React, { useEffect, useState } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Switch,
  Typography,
} from '@mui/material'

import { Button } from '../components/Button/Button'

import { useConsent } from './CookieConsentContext'
import { hasGpcSignal } from './consentUtils'

export const CookieConsentDialog: React.FC = () => {
  const { consent, preferencesOpen, closePreferences, updateConsent, acceptAll } = useConsent()

  const [analytics, setAnalytics] = useState(consent?.categories.analytics ?? false)
  const [marketing, setMarketing] = useState(consent?.categories.marketing ?? false)

  const gpcActive = hasGpcSignal()

  // Sync toggle state when consent changes or dialog opens
  useEffect(() => {
    if (preferencesOpen) {
      setAnalytics(consent?.categories.analytics ?? false)
      setMarketing(consent?.categories.marketing ?? false)
    }
  }, [preferencesOpen, consent])

  const handleSave = () => {
    updateConsent({ analytics, marketing })
    closePreferences()
  }

  const handleAcceptAll = () => {
    acceptAll()
    closePreferences()
  }

  return (
    <Dialog open={preferencesOpen} onClose={closePreferences} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Cookie Preferences</DialogTitle>
      <DialogContent dividers>
        {gpcActive && (
          <StyledGpcNotice>
            <Typography variant="body2">
              Your browser has sent a Global Privacy Control signal. Marketing cookies have been automatically disabled.
            </Typography>
          </StyledGpcNotice>
        )}

        <StyledCategory>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>Essential Cookies</Typography>
            <Typography variant="caption" color="text.secondary">
              Required for the website to function. Cannot be disabled.
            </Typography>
          </Box>
          <Switch checked disabled />
        </StyledCategory>

        <StyledCategory>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>Analytics Cookies</Typography>
            <Typography variant="caption" color="text.secondary">
              Help us understand how visitors interact with our website (Google Analytics).
            </Typography>
          </Box>
          <Switch
            checked={analytics}
            onChange={(_, checked) => setAnalytics(checked)}
          />
        </StyledCategory>

        <StyledCategory>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>Marketing Cookies</Typography>
            <Typography variant="caption" color="text.secondary">
              Used for advertising and tracking across websites (Meta Pixel).
            </Typography>
          </Box>
          <Switch
            checked={marketing}
            onChange={(_, checked) => setMarketing(checked)}
            disabled={gpcActive}
          />
        </StyledCategory>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button outline onClick={handleSave} size="small">
          Save Preferences
        </Button>
        <Button primary onClick={handleAcceptAll} size="small">
          Accept All
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const StyledCategory = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${pxToRem(12)} 0`,
  borderBottom: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.08)',

  '&:last-child': {
    borderBottom: 'none',
  },
})

const StyledGpcNotice = styled(Box)(({ theme }) => ({
  padding: pxToRem(12),
  marginBottom: pxToRem(12),
  backgroundColor: theme.palette.info.light || '#e3f2fd',
  borderRadius: pxToRem(8),
}))
